import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Copy, Check, MessageCircle, Twitter, Instagram, Share2, ArrowDown,
} from "lucide-react";
import { useLang } from "@/lib/useLang";
import { STRINGS } from "@/lib/i18n";
import Analytics from "@/lib/analytics";

const cpLength = (s) => [...(s || "")].length;
const truncate = (s, n) => (cpLength(s) <= n ? s : [...s].slice(0, n).join("") + "…");

export const ThankYouShare = ({ submittedMessage, onBackToFeed }) => {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://www.thenextcm.com";
  const directLink = submittedMessage?.id
    ? `${origin}/m/${submittedMessage.id}`
    : origin;
  const preview = truncate(submittedMessage?.message || "", 120);
  const dict = STRINGS[lang] || STRINGS.en;
  const shareText = typeof dict.invite_share_text === "function"
    ? dict.invite_share_text(preview)
    : `I just posted this on Kerala Speaks. Upvote if you agree: "${preview}"`;
  const fullText = `${shareText} ${directLink}`;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(directLink)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      Analytics.messageShare("copy", "thank_you");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Kerala Speaks", text: shareText, url: directLink });
        Analytics.messageShare("native", "thank_you");
        return;
      } catch { /* user dismissed */ }
    }
    handleCopy();
  };

  const handleInstagramStory = async () => {
    try { await navigator.clipboard.writeText(fullText); } catch { /* ignore */ }
    Analytics.messageShare("instagram", "thank_you");
    try {
      const a = document.createElement("a");
      a.href = "instagram://library?LocalIdentifier=";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* ignore */ }
  };

  return (
    <section className="fade-up space-y-6" data-testid="thank-you-share">
      {/* THANK YOU header */}
      <div
        className="brutal-card p-6 md:p-10"
        style={{ background: "#0A0A0A", color: "#FDFBF7" }}
        data-testid="thanks-block"
      >
        <p
          className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] mb-3"
          style={{ color: "#FF9933" }}
          data-testid="thanks-eyebrow"
        >
          {t("thanks_eyebrow")}
        </p>
        <h2
          className="font-display uppercase leading-[0.9] tracking-tight mb-3"
          style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#FDFBF7" }}
          data-testid="thanks-heading"
        >
          {t("thanks_heading")}
        </h2>
        <p className="text-base md:text-xl font-medium leading-relaxed max-w-2xl" style={{ color: "#FDFBF7" }} data-testid="thanks-sub">
          {t("thanks_sub")}
        </p>
      </div>

      {/* Preview of their message */}
      {submittedMessage?.message && (
        <div
          className="brutal-card p-5 md:p-6"
          style={{ background: "#FFF7E6" }}
          data-testid="your-message-preview"
        >
          <p className="text-xs uppercase font-extrabold tracking-[0.3em] text-neutral-600 mb-2">
            ★ YOUR MESSAGE ★
          </p>
          <p className="text-base md:text-lg font-medium leading-relaxed break-words" data-testid="preview-text">
            “{submittedMessage.message}”
          </p>
        </div>
      )}

      {/* INVITE block */}
      <div className="brutal-card p-5 md:p-7" data-testid="invite-block">
        <h3
          className="font-display uppercase leading-none"
          style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
          data-testid="invite-heading"
        >
          {t("invite_heading")}
        </h3>
        <p className="text-sm md:text-base font-medium text-neutral-700 mt-2 mb-5 max-w-2xl">
          {t("invite_sub")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <button
            type="button"
            className="brutal-btn"
            onClick={handleNativeShare}
            style={{ background: "#0A0A0A", color: "#FF9933", borderColor: "#0A0A0A" }}
            data-testid="share-your-message-btn"
          >
            <Share2 size={18} strokeWidth={3} className="mr-2" />
            {t("share_your_message_btn")}
          </button>
          <a
            className="brutal-btn"
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => Analytics.messageShare("whatsapp", "thank_you")}
            style={{ background: "#138808", borderColor: "#0A0A0A", color: "#FFFFFF" }}
            data-testid="invite-whatsapp-btn"
          >
            <MessageCircle size={18} strokeWidth={3} className="mr-2" /> {t("whatsapp")}
          </a>
          <a
            className="brutal-btn"
            href={xUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => Analytics.messageShare("twitter", "thank_you")}
            data-testid="invite-twitter-btn"
          >
            <Twitter size={18} strokeWidth={3} className="mr-2" /> {t("post_x")}
          </a>
          <button
            type="button"
            className="brutal-btn"
            onClick={handleInstagramStory}
            style={{
              background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
              color: "#FFFFFF",
              borderColor: "#0A0A0A",
            }}
            data-testid="invite-instagram-btn"
          >
            <Instagram size={18} strokeWidth={3} className="mr-2" /> {t("instagram_story_btn")}
          </button>
          <button
            type="button"
            className="brutal-btn"
            onClick={handleCopy}
            style={copied ? { background: "#FF9933", color: "#0A0A0A" } : undefined}
            data-testid="invite-copy-btn"
          >
            {copied ? <Check size={18} strokeWidth={3} className="mr-2" /> : <Copy size={18} strokeWidth={3} className="mr-2" />}
            {copied ? t("copied") : t("copy_direct_link_btn")}
          </button>
        </div>

        {/* Direct link preview */}
        <div
          className="mt-4 text-xs font-bold tracking-wider break-all border-2 border-dashed border-neutral-300 px-3 py-2"
          data-testid="direct-link-preview"
          style={{ color: "#0A0A0A" }}
        >
          {directLink}
        </div>
      </div>

      {/* BACK TO FEED */}
      <div className="brutal-card p-5 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="feed-cta">
        <div>
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
            ★ KEEP READING ★
          </p>
          <h3
            className="font-display uppercase leading-none mt-2"
            style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)" }}
            data-testid="see-feed-heading"
          >
            {t("see_feed_heading")}
          </h3>
        </div>
        <button
          type="button"
          onClick={onBackToFeed}
          className="brutal-btn shrink-0"
          style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
          data-testid="back-to-feed-btn"
        >
          <ArrowDown size={18} strokeWidth={3} className="mr-2" /> {t("see_feed_btn")}
        </button>
      </div>
    </section>
  );
};

export default ThankYouShare;
